import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Card, Form, Spinner } from "react-bootstrap";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ReactECharts from "echarts-for-react";
import "react-toastify/dist/ReactToastify.css";
import "./FinanceReportsAnalytics.css";

const CHART_COLORS = [
  "#3B82F6",
  "#06B6D4",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#6366F1",
  "#0EA5E9",
  "#A78BFA",
  "#22D3EE",
  "#818CF8",
];

const FinanceReportsAnalytics = () => {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [timePeriod, setTimePeriod] = useState("Monthly");
  const [academicYear, setAcademicYear] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [periodApiData, setPeriodApiData] = useState([]);
  const [pieViewMode, setPieViewMode] = useState("SUMMARY");

  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        setLoadingAcademicYears(true);

        const response = await axios.post(`${baseUrl}/financialyearprocedure/`, {
          action: "READ",
          school_id: userObj.school_id,
        });

        const data = Array.isArray(response.data) ? response.data : [];
        setAcademicYears(data);

        const activeYear = data.find((item) => item.is_active === true);
        if (activeYear) {
          setAcademicYear(String(activeYear.fy_id));
        } else if (data.length > 0) {
          setAcademicYear(String(data[0].fy_id));
        } else {
          setAcademicYear("");
        }
      } catch (error) {
        console.error("Error fetching financial years:", error);
        setAcademicYears([]);
        setAcademicYear("");
      } finally {
        setLoadingAcademicYears(false);
      }
    };

    if (baseUrl && userObj?.school_id) {
      fetchAcademicYears();
    }
  }, [baseUrl, userObj?.school_id]);

  useEffect(() => {
    const fetchChartData = async () => {
      if (!academicYear || !userObj?.school_id || !baseUrl) return;

      try {
        setChartLoading(true);
        setPieViewMode("SUMMARY");

        const response = await axios.post(
          `${baseUrl}/finance/get_period_head_summary`,
          {
            school_id: userObj.school_id,
            fy_id: Number(academicYear),
            period_type: timePeriod.toUpperCase(),
          }
        );

        const responseData = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        setPeriodApiData(responseData);
      } catch (error) {
        console.error("Error fetching finance period summary:", error);
        setPeriodApiData([]);
        toast.error("Failed to load chart data");
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [academicYear, timePeriod, baseUrl, userObj?.school_id]);

  const selectedAcademicYearData = useMemo(() => {
    return (
      academicYears.find((item) => String(item.fy_id) === String(academicYear)) ||
      null
    );
  }, [academicYears, academicYear]);

  const dashboardData = useMemo(() => {
    return {
      opening_balance: Number(selectedAcademicYearData?.opening_balance || 0),
      current_balance: Number(selectedAcademicYearData?.current_balance || 0),
      total_expense: Number(selectedAcademicYearData?.total_expense || 0),
      total_income: Number(selectedAcademicYearData?.total_income || 0),
    };
  }, [selectedAcademicYearData]);

  const normalizedChartData = useMemo(() => {
    if (!Array.isArray(periodApiData) || periodApiData.length === 0) return [];

    if (timePeriod === "Monthly") {
      return periodApiData.map((item) => ({
        label: item.month || "",
        expense: Number(item.expense || 0),
        income: Number(item.income || 0),
        expense_heads: Array.isArray(item.expense_heads) ? item.expense_heads : [],
        income_heads: Array.isArray(item.income_heads) ? item.income_heads : [],
      }));
    }

    if (timePeriod === "Quarterly") {
      return periodApiData.map((item) => ({
        label: item.quarter || "",
        expense: Number(item.expense || 0),
        income: Number(item.income || 0),
        expense_heads: Array.isArray(item.expense_heads) ? item.expense_heads : [],
        income_heads: Array.isArray(item.income_heads) ? item.income_heads : [],
      }));
    }

    if (timePeriod === "Yearly") {
      return periodApiData.map((item) => ({
        label: item.financial_year || "FY",
        expense: Number(item.expense || 0),
        income: Number(item.income || 0),
        expense_heads: Array.isArray(item.expense_heads) ? item.expense_heads : [],
        income_heads: Array.isArray(item.income_heads) ? item.income_heads : [],
      }));
    }

    return [];
  }, [periodApiData, timePeriod]);

  const barChartData = useMemo(() => {
    return normalizedChartData.map((item) => ({
      period: item.label,
      Expense: Number(item.expense || 0),
      Income: Number(item.income || 0),
    }));
  }, [normalizedChartData]);

  const incomeHeadPieData = useMemo(() => {
    const incomeHeadMap = {};

    normalizedChartData.forEach((period) => {
      (period.income_heads || []).forEach((head) => {
        const key = head.account_head_name || "Unknown";
        incomeHeadMap[key] =
          (incomeHeadMap[key] || 0) + Number(head.total_income || 0);
      });
    });

    return Object.entries(incomeHeadMap)
      .map(([name, value], index) => ({
        name,
        value,
        itemStyle: { color: CHART_COLORS[index % CHART_COLORS.length] },
      }))
      .sort((a, b) => b.value - a.value);
  }, [normalizedChartData]);

  const expenseHeadPieData = useMemo(() => {
    const expenseHeadMap = {};

    normalizedChartData.forEach((period) => {
      (period.expense_heads || []).forEach((head) => {
        const key = head.account_head_name || "Unknown";
        expenseHeadMap[key] =
          (expenseHeadMap[key] || 0) + Number(head.total_expense || 0);
      });
    });

    return Object.entries(expenseHeadMap)
      .map(([name, value], index) => ({
        name,
        value,
        itemStyle: { color: CHART_COLORS[index % CHART_COLORS.length] },
      }))
      .sort((a, b) => b.value - a.value);
  }, [normalizedChartData]);

  const summaryPieData = useMemo(() => {
    const totalIncome = incomeHeadPieData.reduce(
      (sum, item) => sum + Number(item.value || 0),
      0
    );
    const totalExpense = expenseHeadPieData.reduce(
      (sum, item) => sum + Number(item.value || 0),
      0
    );

    return [
      {
        name: "Income",
        value: totalIncome,
        itemStyle: { color: "#06B6D4" },
      },
      {
        name: "Expense",
        value: totalExpense,
        itemStyle: { color: "#8B5CF6" },
      },
    ];
  }, [incomeHeadPieData, expenseHeadPieData]);

  const pieChartTitle = useMemo(() => {
    if (pieViewMode === "INCOME") return "Income Heads";
    if (pieViewMode === "EXPENSE") return "Expense Heads";
    return "Income vs Expense";
  }, [pieViewMode]);

  const activePieData = useMemo(() => {
    if (pieViewMode === "INCOME") return incomeHeadPieData;
    if (pieViewMode === "EXPENSE") return expenseHeadPieData;
    return summaryPieData;
  }, [pieViewMode, incomeHeadPieData, expenseHeadPieData, summaryPieData]);

  const formatCurrency = (amount) =>
    `₹ ${Number(amount || 0).toLocaleString("en-IN")}`;

  const pieOption = useMemo(() => {
    const noData =
      !activePieData ||
      activePieData.length === 0 ||
      activePieData.every((item) => Number(item.value || 0) === 0);

    return {
      backgroundColor: "transparent",
      title: {
        text: noData ? "No data" : pieChartTitle,
        left: "center",
        top: 14,
        textStyle: {
          fontSize: 16,
          fontWeight: 700,
          color: "#1e293b",
        },
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const val = params?.value || 0;
          const percent = params?.percent ?? 0;
          return `${params.name}<br/>${formatCurrency(val)}${
            pieViewMode === "SUMMARY" ? ` (${percent}%)` : ""
          }`;
        },
      },
      legend: {
        show: false,
      },
      series: [
        {
          name: pieChartTitle,
          type: "pie",
          radius: pieViewMode === "SUMMARY" ? ["45%", "70%"] : ["38%", "72%"],
          center: ["50%", "55%"],
          avoidLabelOverlap: true,
          minAngle: 6,
          label: {
            show: true,
            fontSize: 10,
            color: "#334155",
            formatter:
              pieViewMode === "SUMMARY"
                ? "{b}\n\n{d}%"
                : (params) => {
                    const name = params.name || "";
                    return name.length > 16 ? `${name.slice(0, 16)}...` : name;
                  },
          },
          labelLine: {
            show: true,
            length: 8,
            length2: 8,
          },
          emphasis: {
            scale: true,
            scaleSize: 5,
          },
          data: noData ? [] : activePieData,
        },
      ],
    };
  }, [activePieData, pieChartTitle, pieViewMode]);

  const onPieEvents = {
    click: (params) => {
      if (pieViewMode === "SUMMARY") {
        if (params?.name === "Income") {
          setPieViewMode("INCOME");
        } else if (params?.name === "Expense") {
          setPieViewMode("EXPENSE");
        }
      }
    },
  };

  return (
    <Container fluid className="finance-report-page">
      <ToastContainer position="top-right" autoClose={2500} />

      <Row className="g-2">
        <Col lg={12}>
          <Card className="filter-card report-sticky-top">
            <Card.Body className="py-2">
              <Row className="align-items-end g-2">
                <Col lg={4} md={6} sm={12}>
                  <Form.Group>
                    <Form.Label className="filter-label">Financial Year</Form.Label>
                    <Form.Select
                      value={academicYear}
                      onChange={(e) => setAcademicYear(e.target.value)}
                      disabled={loadingAcademicYears}
                      className="finance-select compact-select"
                    >
                      <option value="">
                        {loadingAcademicYears
                          ? "Loading Financial Years..."
                          : "Select Financial Year"}
                      </option>
                      {academicYears.map((year, index) => (
                        <option key={year?.fy_id || index} value={String(year?.fy_id)}>
                          {year?.fy_name} {year?.is_active ? "(Active)" : ""}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col lg={3} md={6} sm={12}>
                  <Form.Group>
                    <Form.Label className="filter-label">Time Period</Form.Label>
                    <Form.Select
                      value={timePeriod}
                      onChange={(e) => setTimePeriod(e.target.value)}
                      className="finance-select compact-select"
                    >
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Yearly">Yearly</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="report-scroll-body">
            {loadingAcademicYears || chartLoading ? (
              <Card className="placeholder-card">
                <Card.Body className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </Card.Body>
              </Card>
            ) : (
              <>
                <Row className="summary-row-compact g-2">
                  <Col xl={3} lg={3} md={6} sm={6}>
                    <Card className="summary-card summary-card-blue h-100">
                      <Card.Body>
                        <div className="summary-title">Opening Balance</div>
                        <div className="summary-amount">
                          {formatCurrency(dashboardData.opening_balance)}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xl={3} lg={3} md={6} sm={6}>
                    <Card className="summary-card summary-card-green h-100">
                      <Card.Body>
                        <div className="summary-title">Current Balance</div>
                        <div className="summary-amount">
                          {formatCurrency(dashboardData.current_balance)}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xl={3} lg={3} md={6} sm={6}>
                    <Card className="summary-card summary-card-red h-100">
                      <Card.Body>
                        <div className="summary-title">Total Expense</div>
                        <div className="summary-amount">
                          {formatCurrency(dashboardData.total_expense)}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xl={3} lg={3} md={6} sm={6}>
                    <Card className="summary-card summary-card-yellow h-100">
                      <Card.Body>
                        <div className="summary-title">Total Income</div>
                        <div className="summary-amount">
                          {formatCurrency(dashboardData.total_income)}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="chart-row-compact g-2">
                  <Col xl={6} lg={12} md={12}>
                    <Card className="report-card report-card-sm h-100">
                      <Card.Header className="report-card-header">
                        {timePeriod} Comparison
                      </Card.Header>
                      <Card.Body className="chart-card-body compact-chart-body">
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={barChartData} barCategoryGap={14}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                            <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend wrapperStyle={{ fontSize: "12px" }} />
                            <Bar
                              dataKey="Expense"
                              fill="#8B5CF6"
                              radius={[5, 5, 0, 0]}
                            />
                            <Bar
                              dataKey="Income"
                              fill="#06B6D4"
                              radius={[5, 5, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xl={6} lg={12} md={12}>
                    <Card className="report-card report-card-sm h-100">
                      <Card.Header className="report-card-header">
                        Finance Head Distribution
                      </Card.Header>

                      <Card.Body className="echart-card-body">
                        <ReactECharts
                          option={pieOption}
                          style={{ height: "280px", width: "100%" }}
                          notMerge={true}
                          lazyUpdate={true}
                          onEvents={onPieEvents}
                        />

                        <div className="pie-custom-legend">
                          <button
                            type="button"
                            className={`pie-legend-btn ${
                              pieViewMode === "INCOME" ? "active income" : ""
                            }`}
                            onClick={() => setPieViewMode("INCOME")}
                          >
                            Income
                          </button>

                          <button
                            type="button"
                            className={`pie-legend-btn ${
                              pieViewMode === "EXPENSE" ? "active expense" : ""
                            }`}
                            onClick={() => setPieViewMode("EXPENSE")}
                          >
                            Expense
                          </button>

                          <button
                            type="button"
                            className={`pie-legend-btn ${
                              pieViewMode === "SUMMARY" ? "active summary" : ""
                            }`}
                            onClick={() => setPieViewMode("SUMMARY")}
                          >
                            Summary
                          </button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default FinanceReportsAnalytics;